import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import SigninModal from '../modal/SigninModal';
import Otp from '../modal/Otp';
import socketClient from '../utils/socketClient';

interface AuthContextType {
  isAuthenticated: boolean;
  setIsAuthenticated: (value: boolean) => void;
  openSigninModal: () => void;
  checkAuth: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isSigninModalOpen, setIsSigninModalOpen] = useState<boolean>(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState<boolean>(false);
  const [mobileNumber, setMobileNumber] = useState<string>("");

  useEffect(() => {
    const checkAuthStatus = () => {
      const token = localStorage.getItem("authToken");
      const authed = !!token;
      setIsAuthenticated(authed);
      if (authed) {
        socketClient.connect();
      } else {
        socketClient.disconnect();
      }
    };

    checkAuthStatus();
    window.addEventListener('storage', checkAuthStatus);
    return () => {
      window.removeEventListener('storage', checkAuthStatus);
    };
  }, []);

  const openSigninModal = useCallback(() => {
    setIsSigninModalOpen(true);
  }, []);

  const checkAuth = useCallback(() => {
    const token = localStorage.getItem("authToken");
    return !!token;
  }, []);

  const handleGetStarted = () => {
    setIsSigninModalOpen(false);
    setIsOtpModalOpen(true);
  };

  const handleOtpVerified = () => {
    setIsOtpModalOpen(false);
    setIsAuthenticated(true);
    socketClient.connect();
  };

  const handleCloseSignin = () => {
    setIsSigninModalOpen(false);
    setMobileNumber("");
  };

  const handleCloseOtp = () => {
    setIsOtpModalOpen(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        openSigninModal,
        checkAuth
      }}
    >
      {children}

      <SigninModal
        isOpen={isSigninModalOpen}
        onClose={handleCloseSignin}
        onGetStarted={handleGetStarted}
        mobileNumber={mobileNumber}
        setMobileNumber={setMobileNumber}
      />

      <Otp
        isOpen={isOtpModalOpen}
        onClose={handleCloseOtp}
        mobileNumber={mobileNumber}
        onVerify={handleOtpVerified}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
